import type {
  Address,
  CountryCode,
  Customer,
  Product,
  ProductAttributeSearchFilterOption,
  SeoDetails,
  WovarSeoDetails,
} from "@commercekit/api";
import type { SearchWovarProductsRequestInput, WovarCheckoutAddressRequestInput } from "@commercekit/api/lib.server";
import { typedBoolean } from "@commercekit/shared";
import type { MetaDescriptor } from "@remix-run/node";
import { useMatches } from "@remix-run/react";
import { useMemo } from "react";

const DEFAULT_REDIRECT = "/";

export function validateRedirect(redirect: string | null | undefined, defaultUrl: string) {
  if (redirect?.startsWith("/") && redirect[1] !== "/") {
    return redirect;
  }

  return defaultUrl;
}
export const pathResolve = (...path: string[]) => "/" + path.map((p) => p.split("/").filter(typedBoolean).join("/")).join("/");
export const productPath = ({ path, categories }: Pick<Product, "path" | "categories">) => {
  return pathResolve("shop", categories[0]?.fullPath || "product", path);
};
/**
 * This should be used any time the redirect path is user-provided
 * (Like the query string on our login/signup pages). This avoids
 * open-redirect vulnerabilities.
 * @param {string} to The redirect destination
 * @param {string} defaultRedirect The redirect to use if the `to` is unsafe.
 */
export function safeRedirect(to: FormDataEntryValue | string | null | undefined, defaultRedirect: string = DEFAULT_REDIRECT) {
  if (!to || typeof to !== "string") {
    return defaultRedirect;
  }

  if (!to.startsWith("/") || to.startsWith("//")) {
    return defaultRedirect;
  }

  return to;
}

/**
 * This base hook is used in other hooks to quickly search for specific data
 * across all loader data using useMatches.
 * @param {string} id The route id
 * @returns {JSON|undefined} The router data or undefined if not found
 */
export function useMatchesData(id: string): Record<string, unknown> | undefined {
  const matchingRoutes = useMatches();
  const route = useMemo(() => matchingRoutes.find((route) => route.id === id), [matchingRoutes, id]);
  return route?.data;
}

export const graphqlSeo = ({
  pageTitle: title,
  metaDescription: description,
  metaKeywords: keywords,
}: SeoDetails): MetaDescriptor => ({
  title,
  description,
  keywords,
});

export const graphqlWovarSeoDetails = ({
  pageTitle: title,
  metaDescription: description,
  metaKeywords: keywords,
  headingTag: tag,
}: WovarSeoDetails): MetaDescriptor => ({
  title,
  description,
  keywords,
  tag,
});

export const stringsFormat = <T>(arr: Array<T>, separator = ", ", filterFn: (v: T, i: number) => boolean = Boolean) =>
  arr.filter(filterFn).join(separator);
export type CategoryFilter = Record<string, Record<string, boolean>>;

export type ProductSearchFilterType = "checkbox" | "range";
export interface ProductSearchFilterGroupedOption extends ProductAttributeSearchFilterOption {
  groupName: string;
  type: ProductSearchFilterType;
}

export interface ProductAttributeSearchFilterGrouped {
  name: string;
  type: ProductSearchFilterType;
  options: ProductSearchFilterGroupedOption[];
}

export const categoryFilter = () => {
  const str2obj = (search: URLSearchParams): CategoryFilter => {
    const query = decodeURIComponent(search.get("filter") || "");
    const result: CategoryFilter = {};
    const filters = query.split(";") || [];
    filters.forEach((group) => {
      const [key, values] = group.split(":");
      if (key && values) {
        result[key] = {};
        values.split(",").forEach((value) => {
          result[key][value] = true;
        });
      }
    });
    return result;
  };
  const obj2str = (obj: CategoryFilter) =>
    encodeURIComponent(
      Object.entries(obj)
        .map(
          ([group, values]) =>
            `${group}:${Object.entries(values)
              .filter(([, value]) => !!value)
              .map(([key]) => key)
              .join(",")}`
        )
        .join(";")
    );
  const str2arr = (str: string): SearchWovarProductsRequestInput["productAttributes"] => {
    if (!str) return [];
    return decodeURIComponent(str)
      .split(";")
      .filter(typedBoolean)
      .map((i) => {
        const [key, value] = i.split(":");
        return {
          key,
          value: value.split(",").filter(typedBoolean),
        };
      })
      .filter((i) => i.value.length);
  };

  const checkedOptions2str = (options: ProductSearchFilterGroupedOption[]) => {
    const checkedRecords = options.reduce((pre: Record<string, Array<string>>, { value, groupName }) => {
      !pre[groupName] ? (pre[groupName] = [value]) : pre[groupName].push(value);
      return pre;
    }, {});

    return encodeURIComponent(
      Object.entries(checkedRecords)
        .map(([groupName, values]) => [groupName, values.join(",")].join(":"))
        .join(";")
    );
  };

  return { str2obj, obj2str, str2arr, checkedOptions2str };
};

export const form2Obj = (formData: FormData, nameTrans: (v: string) => string): WovarCheckoutAddressRequestInput => ({
  company: formData.get(nameTrans("companyName")) as string,
  firstName: formData.get(nameTrans("firstName")) as string,
  lastName: formData.get(nameTrans("lastName")) as string,
  email: formData.get(nameTrans("email")) as string,
  phone: `${formData.get("phoneCountryCode")}${formData.get(nameTrans("phone"))}`,
  countryCode: formData.get(nameTrans("countryCode")) as keyof typeof CountryCode,
  postalCode: formData.get(nameTrans("postalCode")) as string,
  address1: formData.get(nameTrans("address1")) as string,
  address2:
    (formData.get(nameTrans("address2")) as string) ||
    (`${formData.get(nameTrans("houseNumber"))} ${formData.get(nameTrans("additionalStreetInfo"))}` as string),
  city: formData.get(nameTrans("city")) as string,
  // companyName: formData.get(nameTrans("companyName")) as string,
  purchaseReference: formData.get(nameTrans("purchaseReference")) as string,
  vatNumber: formData.get(nameTrans("vatNumber")) as string,
  kvkNumber: formData.get(nameTrans("kvkNumber")) as string,
});

export const obj2form = (type: "shipping" | "billing", address: Address | null, customer: Customer | null) => ({
  // data from customer
  [`${type}_email`]: customer?.email || "",
  [`${type}_phone`]: customer?.phone || "",
  // data from address
  [`${type}_company`]: address?.company || "",
  [`${type}_firstName`]: address?.firstName || "",
  [`${type}_lastName`]: address?.lastName || "",
  [`${type}_countryCode`]: address?.countryCode || "",
  [`${type}_postalCode`]: address?.postalCode || "",
  [`${type}_address1`]: address?.address1 || "",
  [`${type}_address2`]: address?.address2 || "",
  [`${type}_city`]: address?.city || "",
  [`${type}_purchaseReference`]: address?.purchaseReference || "",
});

export const form2ObjWithPerson = (formData: FormData, type: "shipping" | "billing"): WovarCheckoutAddressRequestInput => {
  const nameTrans = (name: string) => `${type}_${name}`;
  return {
    email: formData.get("email") as string,
    phone: formData.get("phone") as string,
    company: formData.get("company") as string,
    firstName: formData.get("firstName") as string,
    lastName: formData.get("lastName") as string,
    countryCode: formData.get(nameTrans("countryCode")) as string,
    postalCode: formData.get(nameTrans("postalCode")) as string,
    address1: formData.get(nameTrans("address1")) as string,
    address2: formData.get(nameTrans("address2")) as string,
    city: formData.get(nameTrans("city")) as string,
    // companyName: formData.get(nameTrans("companyName")) as string,
    purchaseReference: formData.get(nameTrans("purchaseReference")) as string,
    vatNumber: formData.get(nameTrans("vatNumber")) as string,
    kvkNumber: formData.get(nameTrans("kvkNumber")) as string,
  };
};

enum ShouldReload {
  YES = "YES",
}

export const disabledReload = () => {
  return {
    set: (formData: FormData) => {
      formData.set("_disabledReload", ShouldReload.YES);
      return formData;
    },
    check: (formData: FormData) => formData.get("_disabledReload") === ShouldReload.YES,
  };
};

export const getFullExcessSaleText = (product, text: string) => {
  const regExp = /\(([^)]+)\)/;
  const [workingDaysWParenth, workingDaysWOParenth] = text.match(regExp) ?? ["", ""];

  const replacedText = text
    .replace("{amount}", product.stockLevel.toString())
    .replace("{sku}", product.sku)
    .replace(
      "{date}",
      product.unitsOfMeasure[0].availability.deliveryDate ?? product.unitsOfMeasure[0].availability.workingDays ?? 5
    );
  return product.unitsOfMeasure[0].availability.deliveryDate
    ? replacedText.replace(workingDaysWParenth, "")
    : replacedText.replace(workingDaysWParenth, workingDaysWOParenth);
};

// Phone code utils
export interface PhoneCountryCodeRule {
  phone_code: string;
  country_code: string;
  country_name: string;
  prefixes?: string;
  digits_after_prefix: number;
  max_digits_after_prefix?: number;
}
export interface Country {
  code: string;
  country: string;
  name: string;
  prefixes: string[];
  digitsAfterPrefix: number; // This is the min/max from the table
  maxDigitsAfterPrefix?: number; // For countries with variable length
}

export const numberValidation = (selectedCountry: Country, phoneNumber?: string, forInput?: boolean) => {
  if (!phoneNumber || !selectedCountry) return { isValid: true, message: "" };

  const cleanNumber = phoneNumber.replace(/\D/g, "");
  const { prefixes, digitsAfterPrefix, maxDigitsAfterPrefix } = selectedCountry;

  const matchingPrefix = prefixes.find((prefix) => cleanNumber.startsWith(prefix)) ?? "";
  const prefixLength = matchingPrefix.length;
  const minTotal = prefixLength + digitsAfterPrefix;
  const maxTotal = prefixLength + (maxDigitsAfterPrefix || digitsAfterPrefix);

  // Check prefix validity first if prefixes are required
  if (prefixes.length > 0 && !matchingPrefix) {
    return {
      isValid: false,
      message: minTotal === maxTotal ? "phone_with_code_length_error" : "phone_with_code_length_range_error",
      ...(forInput
        ? {
            minTotal,
            matchingPrefix,
            digitsAfterPrefix,
            ...(minTotal !== maxTotal ? { maxTotal, maxDigitsAfterPrefix } : {}),
          }
        : {}),
    };
  }

  // Check length validity
  const lengthIsValid = maxDigitsAfterPrefix
    ? cleanNumber.length >= minTotal && cleanNumber.length <= maxTotal
    : cleanNumber.length === minTotal;

  if (lengthIsValid) return { isValid: true, message: "" };

  const errorMessage =
    prefixes.length === 0
      ? minTotal === maxTotal
        ? "phone_code_length_error_no_prefix"
        : "phone_code_range_error_no_prefix"
      : minTotal === maxTotal
      ? "phone_with_code_length_error"
      : "phone_with_code_length_range_error";

  return {
    isValid: false,
    message: errorMessage,
    ...(forInput
      ? {
          minTotal,
          matchingPrefix,
          digitsAfterPrefix,
          ...(minTotal !== maxTotal ? { maxTotal, maxDigitsAfterPrefix } : {}),
        }
      : {}),
  };
};
